import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { usePostHog } from "@/hooks/usePostHog";
import { AlertCircle, Check, Mail } from "lucide-react";
import { SMBLogo } from "@/components/ui/smb-logo";

export const AuthForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [inviteCodeError, setInviteCodeError] = useState("");
  const [authError, setAuthError] = useState("");
  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const { toast } = useToast();
  const { trackEvent } = usePostHog();
  const [resendCooldown, setResendCooldown] = useState(false);

  // Invitation state
  const [invitationId, setInvitationId] = useState<string | null>(null);
  const [invitationTeamName, setInvitationTeamName] = useState<string | null>(null);

  // Trial token state
  const [hasTrialToken, setHasTrialToken] = useState(false);

  // Check for invitation and trial token parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inviteParam = urlParams.get('invite');
    const emailParam = urlParams.get('email');
    const trialTokenParam = urlParams.get('trial_token');

    // Check for trial token
    if (trialTokenParam) {
      setHasTrialToken(true);
    }

    if (inviteParam) {
      setInvitationId(inviteParam);
      if (emailParam) {
        setEmail(emailParam);
      }

      // Fetch team name for the invitation banner
      const fetchTeamName = async () => {
        try {
          const { data: invitation } = await supabase
            .from('team_invitations')
            .select(`
              teams (
                name
              )
            `)
            .eq('id', inviteParam)
            .single();

          if (invitation?.teams?.name) {
            setInvitationTeamName(invitation.teams.name);
          }
        } catch (error) {
          console.error('Error fetching team name:', error);
        }
      };

      fetchTeamName();
    }
  }, []);

  const resendConfirmationEmail = async () => {
    if (!email) {
      setAuthError("Your email is not confirmed yet. Enter your email address and try again.");
      return;
    }
    setResendingConfirmation(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: `${window.location.origin}/` }
      });
      if (error) {
        const msg = (error as any)?.message?.toLowerCase?.() || '';
        if (msg.includes('rate limit')) {
          setAuthError('Your email is not confirmed yet. We just sent one — please wait up to 60 seconds before trying again.');
          setResendCooldown(true);
          setTimeout(() => setResendCooldown(false), 60000);
        } else if (msg.includes('timeout')) {
          setAuthError('Your email is not confirmed yet. Email service timed out — please try again in a moment.');
        } else {
          setAuthError('Your email is not confirmed yet. Failed to resend — please try again.');
        }
        console.error('Supabase resend error:', error);
      } else {
        setConfirmationSent(true);
      }
    } catch (err) {
      console.error('Unexpected resend error:', err);
      setAuthError('Your email is not confirmed yet. Failed to resend — please try again.');
    } finally {
      setResendingConfirmation(false);
    }
  };
  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setAuthError(""); // Clear any previous errors

    // Basic client-side password strength check to avoid stuck state on weak passwords
    if (isSignUp) {
      const weak = password.length < 8 || !/[A-Za-z]/.test(password) || !/[0-9]/.test(password);
      if (weak) {
        setAuthError("Password is weak. Use at least 8 characters with letters and numbers.");
        setLoading(false);
        return;
      }
    }

    let timeoutId: number | undefined;

    try {
      // Safety timeout so the button never stays stuck if the network hangs
      timeoutId = window.setTimeout(() => {
        setLoading(false);
        setAuthError("This is taking longer than expected. Please try again in a moment.");
      }, 10000);

      if (isSignUp) {
        const redirectUrl = `${window.location.origin}/`;

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl
          }
        });

        if (error) {
          const msg = (error as any)?.message?.toLowerCase?.() || '';
          if (msg.includes('user already registered')) {
            setAuthError('This email already has an account. Please sign in instead.');
            setIsSignUp(false); // Switch to sign-in mode
            return;
          } else if (msg.includes('weak')) {
            setAuthError('Password is known to be weak and easy to guess. Please choose a stronger password.');
          } else if (msg.includes('rate limit')) {
            setAuthError('Too many requests. Please wait up to 60 seconds and try again.');
          } else if (msg.includes('timeout')) {
            setAuthError('Email service timed out — please try again in a moment.');
          } else {
            setAuthError((error as any).message);
          }
          return;
        }

        // Track user signup in PostHog
        if (data.user) {
          trackEvent('user_signed_up', {
            user_id: data.user.id,
            email: data.user.email,
            has_trial_token: hasTrialToken,
            has_invitation: !!invitationId,
            email_confirmed: !!(data.user && data.session),
          });
        }

        // Check if user is already confirmed (auto-signin enabled)
        if (data.user && data.session) {
          // User is automatically signed in, accept invitation if exists
          if (invitationId) {
            await acceptInvitation();
          }
          // Silent success - AuthProvider will handle navigation to onboarding
          toast({
            title: "Account created!",
            description: "Welcome! Setting up your account...",
          });
        } else {
          // Email confirmation required
          toast({
            title: "Account created!",
            description: "Please check your email to verify your account.",
          });
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          if (error.message.includes("Email not confirmed")) {
            setAuthError("Your email is not confirmed yet.");
            // Automatically resend confirmation email
            setTimeout(() => {
              resendConfirmationEmail();
            }, 500);
          } else if (error.message.includes("Invalid login credentials")) {
            setAuthError("Invalid email or password. Please check your credentials and try again.");
          } else {
            setAuthError(error.message);
          }
        } else {
          // Silent login - no toast needed, AuthProvider will handle routing
          // Accept invitation after successful signin if invitation exists
          if (invitationId) {
            await acceptInvitation();
          }
        }
      }
    } catch (error) {
      setAuthError("An unexpected error occurred. Please try again.");
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleInviteCodeSubmit = async () => {
    if (!inviteCode.trim()) {
      setInviteCodeError("Please enter an invite code");
      return;
    }

    try {
      const response = await supabase.functions.invoke('validate-invite', {
        body: { inviteCode: inviteCode.trim() }
      });

      if (response.error) {
        setInviteCodeError("Error validating invite code");
        return;
      }

      const { valid, message, paymentLink } = response.data;
      
      if (valid && paymentLink) {
        // Store email for after payment
        localStorage.setItem('signup_email', email);
        
        toast({
          title: "Great!",
          description: "Let's get you subscribed and signed up!",
        });
        window.open(paymentLink, '_blank');
      } else {
        setInviteCodeError(message || "Invalid invite code");
      }
    } catch (error) {
      setInviteCodeError("Error validating invite code");
    }
  };

  const acceptInvitation = async () => {
    if (!invitationId) return;
    
    try {
      const { error } = await supabase.functions.invoke('accept-team-invitation', {
        body: { invitationId }
      });
      
      if (error) {
        console.error('Error accepting invitation:', error);
        toast({
          title: "Warning",
          description: "There was an issue accepting your team invitation. Please contact support.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Welcome to the team!",
          description: `You've successfully joined ${invitationTeamName || 'the team'}.`,
        });
        
        // Clear URL parameters after successful acceptance
        const url = new URL(window.location.href);
        url.searchParams.delete('invite');
        url.searchParams.delete('email');
        window.history.replaceState({}, '', url.toString());
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({
        title: "Warning", 
        description: "There was an issue accepting your team invitation. Please contact support.",
        variant: "destructive"
      });
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        setAuthError(error.message);
      }
    } catch (error) {
      setAuthError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-card p-2">
      <div
        className="bg-card border border-border rounded-xl p-6 flex flex-col gap-12"
        style={{
          width: '415px',
          boxShadow: '0px 10px 6px rgba(0, 0, 0, 0.06), 0px 0px 0px 8px rgba(0, 0, 0, 0.03), 0px 1px 2px rgba(0, 0, 0, 0.05)'
        }}
      >
        {/* Header Section */}
        <div className="flex flex-col gap-6">
          {/* Logo */}
          <div className="flex justify-start">
            <SMBLogo className="w-8 h-8 text-foreground" />
          </div>

          {/* Welcome Text */}
          <div className="flex flex-col gap-2">
            <h1 className="text-xl font-semibold text-foreground leading-none">
              {isSignUp ? "Sign Up for an account" : "Welcome Back"}
            </h1>
            <p className="text-base text-muted-foreground leading-6">
              {isSignUp ? "Create your account to get started" : "Enter your credentials to sign in"}
            </p>
          </div>
          
          {/* Invitation Banner */}
          {invitationId && invitationTeamName && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Mail className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <p className="text-sm text-blue-700 leading-5">
                Once logged in, you will be able to accept your invite to <strong>{invitationTeamName}</strong>
              </p>
            </div>
          )}
        </div>

          {/* Form Content */}
        <div className="flex flex-col gap-4">
          {showInviteCode ? (
            <div className="flex flex-col gap-4">
              <div className="text-center">
                <p className="text-foreground text-sm font-medium">
                  An active SMB Broker subscription is required to create an account.
                </p>
                <p className="text-muted-foreground text-sm mt-1">
                  Enter your invite code to proceed with subscription.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground text-sm font-medium">
                  Invite Code
                </Label>
                <Input
                  type="text"
                  placeholder="Enter invite code"
                  value={inviteCode}
                  onChange={(e) => {
                    setInviteCode(e.target.value);
                    setInviteCodeError("");
                  }}
                  className="h-9 px-3 text-sm bg-card border border-border rounded-lg shadow-sm focus:ring-2 focus:ring-ring focus:border-transparent"
                />
                {inviteCodeError && (
                  <p className="text-red-500 text-xs">{inviteCodeError}</p>
                )}
              </div>
              
              <Button
                type="button"
                onClick={handleInviteCodeSubmit}
                className="w-full h-9 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#FDFDFD] text-sm font-medium rounded-lg shadow-lg border-none"
                style={{
                  boxShadow: '0px 0px 0px 1px #1A1A1A, 0px 4px 6px -1px rgba(0, 0, 0, 0.2), 0px 2px 4px -1px rgba(0, 0, 0, 0.1)'
                }}
              >
                Validate Code
              </Button>
              
              <button
                type="button"
                onClick={() => setShowInviteCode(false)}
                className="text-[#7E7E7E] text-sm underline hover:no-underline"
              >
                Back to sign up
              </button>
            </div>
          ) : (
            <>
              {/* Email/Password Form */}
              <form onSubmit={handleAuth} className="flex flex-col gap-3">
                {/* Email Field */}
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-sm font-medium text-foreground"
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-9 px-3 text-sm bg-card border border-border rounded-lg shadow-sm focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <Label
                    htmlFor="password"
                    className="text-sm font-medium text-foreground"
                  >
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-9 px-3 text-sm bg-card border border-border rounded-lg shadow-sm focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                </div>

                {/* Error Banner */}
                {authError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-red-700 leading-5">
                        {authError}
                      </p>
                      {authError.includes("not confirmed") && (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          onClick={resendConfirmationEmail}
                          disabled={resendingConfirmation || resendCooldown}
                          className="h-auto p-0 text-red-600 hover:text-red-700 text-sm font-medium mt-1"
                        >
                          {resendingConfirmation ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></div>
                              Sending...
                            </>
                          ) : resendCooldown ? (
                            <>
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Please wait before trying again
                            </>
                          ) : (
                            <>
                              <Check className="h-3 w-3 mr-1" />
                              Resend confirmation email
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Auth Button */}
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full h-9 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#FDFDFD] text-sm font-medium rounded-lg shadow-lg border-none"
                  style={{
                    boxShadow: '0px 0px 0px 1px #1A1A1A, 0px 4px 6px -1px rgba(0, 0, 0, 0.2), 0px 2px 4px -1px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {loading ? (isSignUp ? "Creating account..." : "Signing in...") : (isSignUp ? "Sign Up" : "Sign In")}
                </Button>
              </form>
            </>
          )}

          {/* Toggle Auth Mode */}
          <div className="text-center text-sm text-foreground">
            {isSignUp ? "Already have an account? " : "Don't have an account? "}
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-foreground underline hover:no-underline"
            >
              {isSignUp ? "Sign In" : "Sign Up"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};